import _ from "lodash";
import {
  BY_ORDER_FILTER_COLUMNS,
  applyByOrderFilters,
  buildLocalDistinctValues,
} from "./byOrderFilterUtils";

const BID_STATUS_FIELD = "bidStatus";

export const HOLDING_BY_ORDER_FILTER_COLUMNS = [
  ...BY_ORDER_FILTER_COLUMNS,
  {
    field: BID_STATUS_FIELD,
    filterOnly: true,
    filterable: true,
    filter: {
      field: BID_STATUS_FIELD,
      label: "Bid Status",
      width: 250,
      height: 300,
      isBidStatus: true,
    },
  },
];

const getGroupKey = (group) =>
  String(group._id || group.key || "");

/** Matches server holding group keys (order/dn, incentive, or unlinked source). */
export const getHoldingBidGroupKey = (bid) => {
  const dnId = bid.debitNote?._id?.toString() || bid.debitNote?.toString();
  const orderId = bid.order?._id?.toString() || bid.order?.toString();
  if (dnId || orderId) return dnId || orderId;

  const incentiveId = bid.incentive?._id?.toString() || bid.incentive?.toString();
  if (incentiveId) return `incentive:${incentiveId}`;

  if (bid.sourceType) return `unlinked:${bid.sourceType}`;
  return null;
};

const bidBelongsToGroup = (bid, group) =>
  getHoldingBidGroupKey(bid) === getGroupKey(group);

const matchesBidStatusFilter = (group, selectedValues) => {
  if (!selectedValues?.length) return false;
  return selectedValues.some((status) => {
    if (status === "HOLD") return (group.holdCount || 0) > 0;
    if (status === "CANCEL") return (group.cancelCount || 0) > 0;
    return false;
  });
};

export const buildHoldingLocalDistinctValues = (orderGroups, col) => {
  if (col.filter?.isBidStatus) {
    const options = [];
    const hasHold = (orderGroups || []).some((g) => (g.holdCount || 0) > 0);
    const hasCancel = (orderGroups || []).some((g) => (g.cancelCount || 0) > 0);
    if (hasHold) options.push({ value: "HOLD", label: "On Hold" });
    if (hasCancel) options.push({ value: "CANCEL", label: "Cancelled" });
    return options;
  }
  return buildLocalDistinctValues(orderGroups, col);
};

export const applyHoldingByOrderFilters = (orderGroups, tableFilterQuery) => {
  if (!orderGroups?.length) return [];
  if (_.isEmpty(tableFilterQuery)) return orderGroups;

  const { bidStatus, ...restQuery } = tableFilterQuery;
  let filtered = applyByOrderFilters(orderGroups, restQuery);

  if (bidStatus != null) {
    const selectedValues = bidStatus?.$in;
    if (!selectedValues?.length) return [];
    filtered = filtered.filter((group) =>
      matchesBidStatusFilter(group, selectedValues),
    );
  }

  return filtered;
};

export const collectHoldingBidIds = (filteredGroups, allBids, action) => {
  if (!filteredGroups?.length || !allBids?.length) return [];

  const statusFilter =
    action === "release"
      ? ["HOLD", "CANCEL"]
      : action === "cancel"
        ? ["HOLD"]
        : action === "revert"
          ? ["CANCEL"]
          : [];

  return allBids
    .filter((bid) => statusFilter.includes(bid.status))
    .filter((bid) => filteredGroups.some((group) => bidBelongsToGroup(bid, group)))
    .map((bid) => bid._id)
    .filter(Boolean);
};

export const getHoldingBulkCounts = (allBids, filteredGroups) => ({
  release: collectHoldingBidIds(filteredGroups, allBids, "release").length,
  cancel: collectHoldingBidIds(filteredGroups, allBids, "cancel").length,
  revert: collectHoldingBidIds(filteredGroups, allBids, "revert").length,
});
